import { ReactNode, useCallback, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { getBuildingById } from 'src/api/get-building.api';
import { ValidateApplicationPolicyReqDto } from 'src/configs/APISchemas/ValidateUserPolicyGraphQL';
import { COMPLIANCE_ERROR_TYPES } from 'src/constants/compliance';
import { ROUTES } from 'src/constants/routes';
import { useLocationSearch } from 'src/helpers/hooks/useLocationSearch';
import { useMount } from 'src/helpers/hooks/useMount';
import { parseQuery } from 'src/helpers/usePrefillApplication';
import { Loader } from 'src/pages/StartApplication/StartApplication.loader';
import {
  updateComplianceProviderAction,
  updateVerifyPolicyInput,
} from 'src/redux/actions/verifyPolicy';
import { getUtmSource } from 'src/redux/selectors/system';
import { validateApplicationPolicy } from '../api/validate-application-policy.api';
import { displayErrorToast } from '../utils/display-error-toast.util';
import { getBuildingAddress } from '../utils/get-building-address.util';
import { getErrorMessage } from '../utils/get-error-message.util';
import { handleApplicationStatus } from '../utils/handle-application-status.util';
import { transformDirectLinkParamsToVerifyPolicyInput } from '../utils/transform-direct-link-params.util';
import { validateCovieDirectLinkParams } from '../utils/validate-covie-direct-link-params.util';

type ComplianceDirectLinkProviderLoadingStatus = 'loading' | 'parsed';

interface ComplianceDirectLinkProviderProps {
  children: ReactNode;
}

export function ComplianceDirectLinkProvider({
  children,
}: ComplianceDirectLinkProviderProps) {
  const navigate = useNavigate();
  const { search } = useLocation();
  const getUrlWithQueryParams = useLocationSearch();

  const dispatch = useDispatch();
  const utmSource = useSelector(getUtmSource);

  const [loadingStatus, setLoadingStatus] =
    useState<ComplianceDirectLinkProviderLoadingStatus>('loading');

  const directLinkSearchParams = useMemo(() => parseQuery(search), [search]);
  const isValidDirectLink = useMemo(
    () => validateCovieDirectLinkParams(directLinkSearchParams),
    [directLinkSearchParams],
  );

  const validateApplicationPolicyAndUpdateVerifyPolicyInput =
    useCallback(async () => {
      const buildingId = directLinkSearchParams.buildingId ?? '';
      const building = await getBuildingById(buildingId);

      const buildingAddress = getBuildingAddress(building);

      const transformedSearchParamsData =
        transformDirectLinkParamsToVerifyPolicyInput(directLinkSearchParams);

      const updatedVerifyPolicyInputPayload = {
        ...transformedSearchParamsData,
        address: buildingAddress,
      };

      dispatch(updateVerifyPolicyInput(updatedVerifyPolicyInputPayload));
      dispatch(
        updateComplianceProviderAction({
          isCovieDirectLink: true,
        }),
      );

      const validateApplicationPolicyReqDto: ValidateApplicationPolicyReqDto = {
        ...transformedSearchParamsData,
        address: buildingAddress,
        utmSource,
      };

      const validateApplicationPolicyResponseData =
        await validateApplicationPolicy(validateApplicationPolicyReqDto);
      const responseLinkId =
        validateApplicationPolicyResponseData?.linkId || null;
      const applicationId =
        validateApplicationPolicyResponseData?.application?.id;
      const responseErrors = validateApplicationPolicyResponseData?.errors;
      const validationErrors =
        validateApplicationPolicyResponseData?.validation?.errors;

      if (!applicationId) {
        if (validationErrors) {
          dispatch(
            updateComplianceProviderAction({
              activeCovieLink: responseLinkId,
            }),
          );
          return;
        }

        if (responseErrors) {
          const errorType = responseErrors[0].type;

          switch (errorType) {
            case COMPLIANCE_ERROR_TYPES.APPLICATION_ALREADY_COMPLIANT: {
              navigate(
                getUrlWithQueryParams(ROUTES.COMPLIANCE_ALREADY_VERIFIED_PATH),
              );
              break;
            }
            default: {
              updateComplianceProviderAction({
                activeCovieLink: responseLinkId,
              });
              break;
            }
          }
          return;
        }

        toast('Unknown response', {
          type: 'error',
        });
        return;
      }

      const routeToNavigateTo = handleApplicationStatus(
        validateApplicationPolicyResponseData,
      );

      if (routeToNavigateTo) {
        navigate(routeToNavigateTo, { replace: true });
        return;
      }

      dispatch(
        updateComplianceProviderAction({
          activeCovieLink: responseLinkId,
        }),
      );
    }, [
      directLinkSearchParams,
      dispatch,
      getUrlWithQueryParams,
      navigate,
      utmSource,
    ]);

  useMount(() => {
    setLoadingStatus('loading');

    if (!isValidDirectLink) {
      setLoadingStatus('parsed');
      return;
    }

    validateApplicationPolicyAndUpdateVerifyPolicyInput()
      .catch((error) => {
        const errorMessage = getErrorMessage(error);
        displayErrorToast(errorMessage);
      })
      .finally(() => {
        setLoadingStatus('parsed');
      });
  });

  if (loadingStatus === 'loading') {
    return <Loader />;
  }

  return <>{children}</>;
}
